import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEOHead from "../components/SEOHead";
import ChatWidget from "../components/ChatWidget";
import OnboardingWizard from "../components/OnboardingWizard";
import {
  Building2,
  Zap,
  Bot,
  Headphones,
  ShoppingCart,
  Home,
  Megaphone,
  Briefcase,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  Mail,
  Facebook,
  Instagram,
  Linkedin,
  Send,
} from "lucide-react";
import marketplaceAgents from "../data/marketplaceAgents";

const SITE_URL = "https://maxemployee.com";

const ICON_MAP = {
  "customer-support": <Headphones className="w-7 h-7 text-amber-500" />,
  ecommerce: <ShoppingCart className="w-7 h-7 text-amber-500" />,
  "real-estate": <Home className="w-7 h-7 text-amber-500" />,
  "marketing-agencies": <Megaphone className="w-7 h-7 text-amber-500" />,
  consultancy: <Briefcase className="w-7 h-7 text-amber-500" />,
};

const CHANNEL_ICON_MAP = {
  WhatsApp: <MessageCircle className="w-3.5 h-3.5" />,
  Telegram: <Send className="w-3.5 h-3.5" />,
  Email: <Mail className="w-3.5 h-3.5" />,
  Discord: <MessageCircle className="w-3.5 h-3.5" />,
  Instagram: <Instagram className="w-3.5 h-3.5" />,
  Facebook: <Facebook className="w-3.5 h-3.5" />,
  "X/Twitter": <Megaphone className="w-3.5 h-3.5" />,
  LinkedIn: <Linkedin className="w-3.5 h-3.5" />,
};

const VERTICALS = marketplaceAgents.map((agent) => ({
  slug: agent.slug,
  icon: ICON_MAP[agent.slug] || <Bot className="w-7 h-7 text-amber-500" />,
  title: agent.name,
  description: agent.description,
  price: agent.price,
  setupFee: agent.setupFee,
  href: agent.href,
  channels: agent.channels.map((label) => ({
    label,
    icon: CHANNEL_ICON_MAP[label] || (
      <MessageCircle className="w-3.5 h-3.5" />
    ),
  })),
}));

const SKILLS = [
  "24/7 autonomous operation",
  "Multi-channel support",
  "Smart escalation to humans",
  "Custom knowledge base",
  "Real-time dashboard",
  "Weekly performance reports",
  "API access",
  "No lock-in contracts",
];

const FAQ_ITEMS = [
  {
    question: "How is this different from Max OS Cloud?",
    answer:
      "Marketplace agents are pre-configured for a specific industry with optimized skills, channels, and workflows. Cloud lets you build a custom agent from scratch.",
  },
  {
    question: "Can I customize my marketplace agent?",
    answer:
      "Yes! You can add custom skills, connect additional channels, and upload your own knowledge base.",
  },
  {
    question: "How long until my agent is active?",
    answer:
      "Self-service activation takes 5 minutes. With Setup Assist, we handle everything within 24-48 hours.",
  },
  {
    question: "What if my industry isn't listed?",
    answer:
      "Start with Max OS Cloud for a custom agent, or contact us about building a new marketplace vertical.",
  },
];

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  const btnId = `faq-btn-${question.replace(/\s+/g, "-").toLowerCase()}`;
  const panelId = `faq-panel-${question.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div className="border border-zinc-800 rounded-lg overflow-hidden">
      <button
        id={btnId}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-controls={panelId}
        className="w-full flex items justify-between text-left px-6 py-4 text-zinc-200 font-medium hover:bg-white/[0.02] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
      >
        <span className="font-mono text-sm">{question}</span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-amber-500 shrink-0 ml-4" aria-hidden="true" />
        ) : (
          <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0 ml-4" aria-hidden="true" />
        )}
      </button>
      {open && (
        <div id={panelId} role="region" aria-labelledby={btnId} className="px-6 pb-4 text-zinc-400 text-sm leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function Marketplace() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 600);
    return () => clearTimeout(timer);
  }, []);

  // Check if onboarding should show (first visit)
  useEffect(() => {
    try {
      const completed = localStorage.getItem("maxemployee.onboarding_wizard_completed");
      if (!completed) {
        setShowOnboarding(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  const handleFirstPurchase = () => {
    setShowOnboarding(false);
    // Could trigger checkout flow here
  };

  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Max OS Agent Marketplace",
    description:
      "Pre-built AI agents for your industry. Activate in minutes, start saving hours. Agents for customer support, e-commerce, real estate, marketing agencies, and consultancy.",
    url: "https://maxemployee.com/marketplace",
    brand: { "@type": "Brand", name: "Max OS" },
    offers: {
      "@type": "AggregateOffer",
      lowPrice: "99",
      highPrice: "249",
      priceCurrency: "USD",
      offerCount: "5",
      availability: "https://schema.org/InStock",
    },
    category: "AI Agent Marketplace",
  };

  const itemListStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "AI Agent Marketplace",
    description: "Browse pre-built AI agents by industry",
    itemListElement: VERTICALS.map((v, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: v.title,
      description: v.description,
      url: `${SITE_URL}${v.href}`,
    })),
  };

  return (
    <>
      <SEOHead
        title="Agent Marketplace | Pre-Built AI Agents for Business"
        description="Browse AI agents by industry: E-commerce, Real Estate, Consultancy, Marketing Agencies, Customer Support. Each agent is pre-trained for your vertical."
        image="https://maxemployee.com/og-image-marketplace.webp"
        url="/marketplace"
        structuredData={[
          productStructuredData,
          itemListStructuredData,
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: "https://maxemployee.com/",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Marketplace",
                item: "https://maxemployee.com/marketplace",
              },
            ],
          },
        ]}
      />
      <Header />

      <main id="main-content" className="min-h-screen bg-[#09090B]">
        {/* Section 1 — Hero */}
        <section className="relative overflow-hidden" aria-labelledby="marketplace-hero-heading">
          {/* Background gradient + pattern */}
          <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 via-transparent to-transparent" aria-hidden="true" />
          <div
            className="absolute inset-0 opacity-[0.03]"
            aria-hidden="true"
            style={{
              backgroundImage: "radial-gradient(circle, #f59e0b 1px, transparent 1px)",
              backgroundSize: "24px 24px",
            }}
          />

          <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-20">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 border border-zinc-800 rounded-full px-4 py-1.5 mb-8">
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-400" aria-hidden="true">
                  🚀
                </span>
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-400">
                  {t('marketplace_page.self_service')}
                </span>
              </div>

              <h1 id="marketplace-hero-heading" className="text-5xl md:text-6xl font-display font-bold text-white leading-[1.1] tracking-tight">
                {t('marketplace_page.title')}
              </h1>

              <p className="mt-6 text-lg md:text-xl text-zinc-400 leading-relaxed max-w-xl">
                {t('marketplace_page.subtitle')}
              </p>

              <div className="mt-10 flex flex-wrap gap-2 sm:gap-4">
                <Link
                  to="/marketplace/customer-support"
                  className="bg-amber-500 hover:bg-amber-400 text-black px-8 py-3.5 font-bold uppercase tracking-wider text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090B]"
                >
                  {t('marketplace_page.browse_agents')}
                </Link>
                <Link
                  to="/pricing"
                  className="border border-zinc-700 hover:border-zinc-500 text-zinc-300 px-8 py-3.5 font-mono uppercase tracking-wider text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090B]"
                >
                  {t('marketplace_page.compare_pricing')}
                </Link>
              </div>
              <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
                {t('marketplace_page.hero_note')}
              </p>
            </div>
          </div>
        </section>

        {/* Breadcrumb nav */}
        <nav aria-label={t('marketplace.breadcrumb')} className="max-w-7xl mx-auto px-6 lg:px-12 pb-8">
          <ol className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">
            <li className="inline">
              <Link to="/" className="hover:text-amber-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500">
                {t('nav.home')}
              </Link>
            </li>
            <li className="mx-3" aria-hidden="true">/</li>
            <li className="inline text-zinc-300" aria-current="page">{t('marketplace_page.breadcrumb_marketplace')}</li>
          </ol>
        </nav>

        {/* Section 2 — How it works */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-20" aria-labelledby="how-it-works-heading">
          <h2 id="how-it-works-heading" className="text-3xl font-display font-bold text-white mb-4">
            {t('marketplace_page.how_it_works')}
          </h2>
          <p className="text-zinc-400 mb-12 max-w-xl">
            {t('marketplace_page.how_it_works_subtitle')}
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                step: "01",
                icon: <Building2 className="w-6 h-6 text-amber-500" />,
                title: t('marketplace_page.step_1_title'),
                desc: t('marketplace_page.step_1_desc'),
              },
              {
                step: "02",
                icon: <Zap className="w-6 h-6 text-amber-500" />,
                title: t('marketplace_page.step_2_title'),
                desc: t('marketplace_page.step_2_desc'),
              },
              {
                step: "03",
                icon: <Bot className="w-6 h-6 text-amber-500" />,
                title: t('marketplace_page.step_3_title'),
                desc: t('marketplace_page.step_3_desc'),
              },
            ].map((item) => (
              <div
                key={item.step}
                className="border border-zinc-800 rounded-xl p-8 hover:border-amber-500/30 transition-colors"
              >
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400 mb-6" aria-hidden="true">
                  {t('marketplace_page.step_label', {defaultValue: 'Step'})} {item.step}
                </div>
                <div className="mb-4" aria-hidden="true">{item.icon}</div>
                <h3 className="text-lg font-display font-bold text-white mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 3 — Vertical cards grid */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-20" aria-labelledby="agents-heading">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <h2 id="agents-heading" className="text-3xl font-display font-bold text-white mb-4">
                {t('marketplace_page.agents_by_industry')}
              </h2>
              <p className="text-zinc-400 max-w-lg">
                {t('marketplace_page.agents_subtitle')}
              </p>
            </div>
          </div>

          <div className={`grid md:grid-cols-2 lg:grid-cols-3 gap-6 ${loading ? "animate-pulse" : ""}`} role="list" aria-label={t('marketplace.industry_agents')}>
            {VERTICALS.map((v) => (
              loading ? (
                <div
                  key={v.slug}
                  className="border border-zinc-800 bg-zinc-950/30 rounded-xl overflow-hidden"
                >
                  <div className="w-full h-40 bg-zinc-800/60" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 w-20 bg-zinc-800/60 rounded" />
                    <div className="h-5 w-2/3 bg-zinc-800/60 rounded" />
                    <div className="h-4 w-full bg-zinc-800/60 rounded" />
                    <div className="h-4 w-5/6 bg-zinc-800/60 rounded" />
                    <div className="h-9 w-28 bg-zinc-800/60 rounded" />
                  </div>
                </div>
              ) : (
                <Link
                  key={v.slug}
                  to={v.href}
                  className="group block rounded-xl p-6 transition-all duration-300 hover:-translate-y-1 border border-transparent hover:border-amber-500"
                  style={{
                    background: "rgba(255,255,255,0.05)",
                    borderRadius: "12px",
                  }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    {v.icon}
                    <h3 className="text-lg font-display font-bold text-white">
                      {v.title}
                    </h3>
                  </div>

                  <p className="text-sm text-zinc-400 leading-relaxed mb-4">
                    {v.description}
                  </p>

                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="text-2xl font-bold text-white">
                      {v.price}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    {v.channels.map((ch) => (
                      <span
                        key={ch.label}
                        className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-zinc-400 border border-zinc-800 rounded px-2 py-1"
                      >
                        {ch.icon}
                        {ch.label}
                      </span>
                    ))}
                  </div>

                  <span className="inline-flex items-center gap-2 text-amber-500 text-sm font-mono uppercase tracking-wider group-hover:gap-3 transition-all">
                    {t('marketplace_page.view_agent')}
                  </span>
                </Link>
              )
            ))}
          </div>
        </section>

        {/* Section 4 — Pre-built skills included */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-20" aria-labelledby="skills-heading">
          <div className="border border-zinc-800 rounded-xl p-10">
            <h2 id="skills-heading" className="text-3xl font-display font-bold text-white mb-4">
              {t('marketplace_page.skills_included')}
            </h2>
            <p className="text-zinc-400 mb-10 max-w-xl">
              {t('marketplace_page.skills_intro')}
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              {SKILLS.map((skill, i) => (
                <div
                  key={skill}
                  className="flex items-center gap-3 py-3 border-b border-zinc-800/50"
                >
                  <div className="w-1.5 h-1.5 bg-amber-500 rounded-full shrink-0" aria-hidden="true" />
                  <span className="text-sm text-zinc-300">{t(`marketplace_page.skill_${i + 1}`)}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 5 — Pricing note */}
        <section className="max-w-7xl mx-auto px-6 lg:px-12 py-12" aria-label={t('marketplace.pricing_information')}>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 border border-zinc-800 rounded-xl px-8 py-6">
            <p className="text-sm text-zinc-400 leading-relaxed max-w-xl">
              {t('marketplace_page.pricing_note')}
            </p>
            <Link
              to="/pricing"
              className="shrink-0 text-amber-500 hover:text-amber-400 font-mono text-sm uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
            >
              {t('marketplace_page.see_full_pricing')}
            </Link>
          </div>
        </section>

        {/* Section 7 — FAQ */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-20" aria-labelledby="faq-heading">
          <h2 id="faq-heading" className="text-3xl font-display font-bold text-white mb-4">
            {t('marketplace_page.faq_title')}
          </h2>
          <p className="text-zinc-400 mb-10 max-w-lg">
            {t('marketplace_page.faq_subtitle')}
          </p>

          <div className="max-w-3xl space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <FaqItem
                key={item.question}
                question={t(`marketplace_page.faq_q${i + 1}`)}
                answer={t(`marketplace_page.faq_a${i + 1}`)}
              />
            ))}
          </div>
        </section>

        {/* Section 7b — Integration Marketplace */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-20" aria-labelledby="integrations-heading">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <h2 id="integrations-heading" className="text-3xl font-display font-bold text-white mb-4">
                {t('marketplace_page.integrations_title')}
              </h2>
              <p className="text-zinc-400 max-w-lg">
                {t('marketplace_page.integrations_subtitle')}
              </p>
            </div>
            <Link
              to="/marketplace/integrations"
              className="mt-4 md:mt-0 shrink-0 text-amber-500 hover:text-amber-400 font-mono text-sm uppercase tracking-wider transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
            >
              {t('marketplace_page.view_all_integrations')}
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: 'Stripe', desc: t('marketplace_page.integration_stripe'), color: '#635BFF', icon: 'S' },
              { name: 'Slack', desc: t('marketplace_page.integration_slack'), color: '#4A154B', icon: 'S' },
              { name: 'Notion', desc: t('marketplace_page.integration_notion'), color: '#000000', icon: 'N' },
              { name: 'GitHub', desc: t('marketplace_page.integration_github'), color: '#181717', icon: 'G' },
              { name: 'HubSpot', desc: t('marketplace_page.integration_hubspot'), color: '#FF7A59', icon: 'H' },
              { name: 'Google Sheets', desc: t('marketplace_page.integration_gsheets'), color: '#34A853', icon: 'G' },
            ].map((ig) => (
              <Link
                key={ig.name}
                to="/marketplace/integrations"
                className="group flex items-center gap-4 border border-zinc-800 rounded-xl p-5 hover:border-zinc-700 transition-colors"
              >
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                  style={{ backgroundColor: `${ig.color}15`, border: `1px solid ${ig.color}30` }}
                >
                  <span className="text-sm font-display font-bold" style={{ color: ig.color }}>
                    {ig.icon}
                  </span>
                </div>
                <div>
                  <h3 className="font-display text-sm uppercase text-white group-hover:text-amber-500 transition-colors">
                    {ig.name}
                  </h3>
                  <p className="text-xs text-zinc-500">{ig.desc}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Section 7 — Final CTA */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-20" aria-labelledby="final-cta-heading">
          <div className="border border-amber-500/20 rounded-xl p-12 text-center bg-gradient-to-b from-amber-500/[0.03] to-transparent">
            <h2 id="final-cta-heading" className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
              {t('marketplace_page.cta_title')}
            </h2>
            <p className="text-zinc-400 mb-10 max-w-lg mx-auto">
              {t('marketplace_page.cta_subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="bg-amber-500 hover:bg-amber-400 text-black px-8 py-3.5 font-bold uppercase tracking-wider text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090B]"
              >
                {t('marketplace_page.talk_to_team')}
              </Link>
              <Link
                to="/pricing"
                className="border border-zinc-700 hover:border-zinc-500 text-zinc-300 px-8 py-3.5 font-mono uppercase tracking-wider text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#09090B]"
              >
                {t('marketplace_page.try_free')}
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-24 border-t border-zinc-900">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-4">// Featured Skills</div>
        <h2 className="font-display text-4xl md:text-5xl uppercase leading-tight mb-4">{t('marketplace_page.skills_for')}<br />{t('marketplace_page.your_agent')}</h2>
        <p className="text-zinc-400 max-w-xl mb-8">
          {t('marketplace_page.skills_desc')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {[
            { name: t('marketplace_page.skill_contract_writing'), cat: t('marketplace_page.skill_cat_legal'), price: t('marketplace_page.skill_price_29'), icon: '⚖️' },
            { name: t('marketplace_page.skill_dynamic_faq'), cat: t('marketplace_page.skill_cat_support'), price: t('marketplace_page.skill_price_free'), icon: '🏢' },
            { name: t('marketplace_page.skill_bank_reconciliation'), cat: t('marketplace_page.skill_cat_finance'), price: t('marketplace_page.skill_price_39'), icon: '💰' },
          ].map((s) => (
            <div key={s.name} className="bg-zinc-950 border border-zinc-900 p-6 hover:border-zinc-700 transition-colors">
              <div className="text-2xl mb-3">{s.icon}</div>
              <h3 className="font-display text-lg uppercase text-white mb-1">{s.name}</h3>
              <p className="font-mono text-xs text-zinc-500 mb-2">{s.cat}</p>
              <p className="font-display text-amber-500">{s.price}</p>
            </div>
          ))}
        </div>
        <Link to="/skills" className="inline-block bg-amber-500 hover:bg-amber-400 text-black px-6 py-3 font-mono text-xs uppercase tracking-wider transition-colors">
          {t('marketplace_page.view_all_skills')}
        </Link>
      </section>
      {/* Chat Widget — Max Sales Agent */}
      <ChatWidget />
      {/* Onboarding Wizard — shown on first visit */}
      {showOnboarding && (
        <OnboardingWizard
          onComplete={handleOnboardingComplete}
          onFirstPurchase={handleFirstPurchase}
        />
      )}
    </>
  );
}
